import { useState, useRef, useEffect } from 'react'
import { DateInput } from './InputFields'
import Calendar from 'react-calendar'
import 'react-calendar/dist/Calendar.css'
import './Calendar.scss'
import moment from 'moment'
import { ArrowRight } from './ArrowRight'
import { ArrowLeft } from './ArrowLeft'
import { ArrowDown } from './ArrowDown'

interface DateRangePickerProps {}

export const DateRangePicker = ({}: DateRangePickerProps) => {
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [calendarKey, setCalendarKey] = useState(0)
  const [calendarOpen, setCalendarOpen] = useState<'from' | 'to' | null>(null)
  const [range, setRange] = useState<[Date, Date] | null>(null)
  const calendarRef = useRef<HTMLDivElement>(null)

  const minDate = moment().toDate()

  const handleCalendarChange = (value: Date | [Date, Date]) => {
    if (Array.isArray(value)) {
      const [start, end] = value
      setFromDate(moment(start).format('DD/MM/YYYY'))
      setToDate(moment(end).format('DD/MM/YYYY'))
      setRange([start, end])
    } else {
      const dateStr = moment(value).format('DD/MM/YYYY')
      if (calendarOpen === 'from') setFromDate(dateStr)
      else if (calendarOpen === 'to') setToDate(dateStr)
    }
  }

  const parseDate = (str: string): Date | null => {
    const m = moment(str, 'DD/MM/YYYY', true)
    return m.isValid() ? m.toDate() : null
  }

  const onFocusInput = (type: 'from' | 'to') => {
    setCalendarOpen(type)
    const start = parseDate(fromDate)
    const end = parseDate(toDate)
    if (start && end) setRange([start, end])
  }

  const handleClear = () => {
    setRange(null)
    setFromDate('')
    setToDate('')
    setCalendarKey((prev) => prev + 1)
  }

  const handleOk = () => {
    setCalendarOpen(null)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node)
      ) {
        setCalendarOpen(null)
      }
    }

    if (calendarOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [calendarOpen])

  return (
    <div className='relative inline-block'>
      <div className='flex items-center gap-[0.4375rem]'>
        <DateInput
          value={fromDate}
          setValue={setFromDate}
          placeholder='From'
          className='md:w-[7.625rem] w-[5.625rem]'
          onFocus={() => onFocusInput('from')}
        />
        <div className='text-[#A0ABBE]'>-</div>
        <DateInput
          value={toDate}
          setValue={setToDate}
          placeholder='To'
          className='md:w-[7.625rem] w-[5.625rem]'
          onFocus={() => onFocusInput('to')}
        />
      </div>

      {calendarOpen && (
        <div
          ref={calendarRef}
          className='absolute left-1/2 -translate-x-1/2 z-50 mt-2 p-4 rounded-lg border border-[#CCE0DD] bg-white shadow-[0_0.0625rem_1rem_0_rgba(0,0,0,0.16)]'
        >
          <Calendar
            key={calendarKey}
            selectRange
            onChange={handleCalendarChange}
            value={range || undefined}
            locale='en-GB'
            minDate={minDate}
            tileDisabled={({ date }) => date < minDate}
            prev2Label={null}
            next2Label={null}
            maxDetail='month'
            minDetail='year'
            formatShortWeekday={(locale, date) => moment(date).format('dd')[0]}
            navigationLabel={({ date }) => (
              <div className='flex items-center font-medium text-black text-sm tracking-wide'>
                {moment(date).format('MMMM, YYYY')}
                <ArrowDown className='ml-2' />
              </div>
            )}
            prevLabel={<ArrowLeft />}
            nextLabel={<ArrowRight />}
            className='custom-calendar'
          />

          <div className='flex justify-end items-center gap-4 mt-2 p-1 h-[1.25rem]'>
            <button
              onClick={handleClear}
              disabled={range === null}
              className={`text-sm leading-tight tracking-wide ${
                range === null ? 'text-[#8C959A]' : 'text-black'
              }`}
            >
              Clear
            </button>
            <button
              onClick={handleOk}
              disabled={range === null}
              className={`text-sm leading-tight tracking-wide ${
                range === null ? 'text-[#8C959A]' : 'text-black'
              }`}
            >
              Ok
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
